// unauthorized.component.ts (Enhanced Version)
import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-unauthorized',
  template: `
  <app-nav-menu></app-nav-menu>
    <div class="unauthorized-container">
      <div class="unauthorized-content">
        <h1>403 - Access Denied</h1>
        <i class="fa fa-lock" style="font-size: 64px; color: #dc3545; margin: 20px;"></i>
        <p>You don't have permission to access this page.</p>
        <p>Please contact your administrator if you believe this is a mistake.</p>

        <div class="button-group">
          <button class="btn btn-primary" (click)="goBack()" [disabled]="!canGoBack">
            <i class="fa fa-arrow-left"></i> Go Back
          </button>
          <button class="btn btn-secondary" (click)="goToDashboard()">
            <i class="fa fa-home"></i> Home
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .unauthorized-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background-color: #f8f9fa;
      padding: 20px;
    }
    .unauthorized-content {
      text-align: center;
      padding: 40px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      max-width: 500px;
      width: 100%;
    }
    h1 {
      color: #dc3545;
      margin-bottom: 20px;
      font-size: 28px;
    }
    p {
      color: #6c757d;
      margin: 10px 0;
      line-height: 1.5;
    }
    .button-group {
      margin-top: 30px;
      display: flex;
      gap: 10px;
      justify-content: center;
      flex-wrap: wrap;
    }
    .btn {
      margin: 5px;
      padding: 10px 25px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.3s ease;
      min-width: 120px;
    }
    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .btn-primary {
      background-color: #FFBB00;
      color: #555;
    }
    .btn-primary:hover:not(:disabled) {
      background-color: #FFAA00;
    }
    .btn-secondary {
      background-color: #6c757d;
      color: white;
    }
    .btn-secondary:hover {
      background-color: #545b62;
    }
    .fa {
      margin-right: 5px;
    }
  `]
})
export class UnauthorizedComponent implements OnInit {
  previousUrl: string = '/flightSearch';
  canGoBack: boolean = false;

  // Permission flags
  hasFlightSearchAccess: boolean = false;
  hasBookingsAccess: boolean = false;
  hasAccessibleRoutes: boolean = false;

  constructor(
    private location: Location,
    private router: Router,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.checkUserPermissions();
    this.determineSafeReturnUrl();
  }

  private checkUserPermissions() {
    // Check what the user can access
    this.hasFlightSearchAccess = this.authService.hasPermission('book');
    this.hasBookingsAccess = this.authService.hasAnyPermission(['bookings', 'viewBookings']);
    this.hasAccessibleRoutes = this.hasFlightSearchAccess || this.hasBookingsAccess;
  }

  private determineSafeReturnUrl() {
    // Get navigation history
    const navigation = this.router.getCurrentNavigation();

    if (navigation.previousNavigation) {
      const previousUrl = navigation.previousNavigation.finalUrl.toString() || '';

      // Check if previous URL is safe
      if (this.isSafeUrl(previousUrl)) {
        this.previousUrl = previousUrl;
        this.canGoBack = true;
        return;
      }
    }

    // Try to get from browser history state
    const historyState = window.history.state as any;
    if (historyState.navigationId > 1) {
      // We have at least one previous page in history
      this.canGoBack = true;
    }

    // Set default based on permissions
    if (this.hasFlightSearchAccess) {
      this.previousUrl = '/flightSearch';
    } else if (this.hasBookingsAccess) {
      this.previousUrl = '/bookingList';
    }
  }

  private isSafeUrl(url: string): boolean {
    if (!url || url === '/' || url === '/home') {
      return false;
    }

    // List of unsafe patterns
    const unsafePatterns = [
      'unauthorized',
      'register',
      'forgot-password',
      'reset-password'
    ];

    // Check if URL contains any unsafe patterns
    return !unsafePatterns.some(pattern => url.includes(pattern));
  }

  goBack() {
    if (this.canGoBack && this.previousUrl) {
      // Navigate to the stored previous URL
      this.router.navigateByUrl(this.previousUrl).catch(() => {
        // Fallback if navigation fails
        this.goToDashboard();
      });
    } else {
      this.goToDashboard();
    }
  }

  goToDashboard() {
    // Navigate based on user permissions
    this.router.navigate(['/flightSearch']);
  }

  goToFlightSearch() {
    this.router.navigate(['/flightSearch']);
  }
}
